import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Row, Col, Form, Card, Spinner, Alert } from 'react-bootstrap';
import { AppContext } from '../AppContext';
import { tre } from '../trebus';
import { FiLogIn, FiUser, FiLock, FiAlertCircle } from 'react-icons/fi';
import { MdDirectionsBus } from 'react-icons/md';

export default function Login() {
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [isButtonHovered, setIsButtonHovered] = useState(false);
    const loginInputRef = useRef(null);

    // Focus login input on mount
    useEffect(() => {
        if (loginInputRef.current) {
            loginInputRef.current.focus();
        }
    }, []);

    // Clear error when user types
    useEffect(() => {
        if (error) setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login, password]);

    /**
     * Do login
     */
    const doLogin = () => {
        if (!login.trim() || !password.trim()) {
            setError('Por favor, introduce usuario y contraseña');
            return;
        }

        setIsLoading(true);
        setError('');

        tre.api('PUT', '/login', {
            context: context,
            params: {
                login: login.trim(),
                password: password
            },
            success: r => {
                // Set global context
                context.setUser(r);

                // Navigate based on user role
                switch (r.role) {
                    case 'ADMIN':
                        navigate('/journey');
                        break;
                    case 'DRIVER':
                        navigate('/journey');
                        break;
                    default:
                        navigate('/calendar');
                }
            },
            error: (err) => {
                setIsLoading(false);
                setError(err.error || 'Usuario o contraseña incorrectos');
            }
        });
    };

    /**
     * Handle form submit
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        doLogin();
    };

    // Render
    return (
        <div
            className="min-vh-100 d-flex align-items-center justify-content-center"
            style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '20px'
            }}
        >
            <div className="container">
                <Row className="justify-content-center">
                    <Col xs={12} sm={10} md={7} lg={5} xl={4}>
                        {/* Logo and Title */}
                        <div className="text-center mb-4">
                            <div
                                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                style={{
                                    width: '80px',
                                    height: '80px',
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(10px)'
                                }}
                            >
                                <MdDirectionsBus
                                    className="text-white"
                                    style={{ fontSize: '42px' }}
                                />
                            </div>
                            <h1 className="text-white fw-bold mb-1" style={{ fontSize: '2rem', letterSpacing: '2px' }}>
                                BOLEBUS
                            </h1>
                            <p className="text-white-50 mb-0">Sistema de Gestión de Viajes</p>
                        </div>

                        {/* Login Card */}
                        <Card
                            className="border-0"
                            style={{
                                borderRadius: '16px',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                            }}
                        >
                            <Card.Body className="p-4 p-md-5">
                                <h4 className="text-center mb-4 text-dark fw-semibold">
                                    Iniciar Sesión
                                </h4>

                                {/* Error Alert */}
                                {error && (
                                    <Alert
                                        variant="danger"
                                        className="d-flex align-items-center py-2 mb-4"
                                        style={{ borderRadius: '10px' }}
                                    >
                                        <FiAlertCircle className="me-2 flex-shrink-0" />
                                        <span>{error}</span>
                                    </Alert>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-4">
                                        <Form.Label className="text-muted small fw-semibold text-uppercase">
                                            Usuario
                                        </Form.Label>
                                        <div className="input-group">
                                            <span
                                                className="input-group-text bg-light border-end-0"
                                                style={{ borderRadius: '10px 0 0 10px' }}
                                            >
                                                <FiUser className="text-muted" />
                                            </span>
                                            <Form.Control
                                                ref={loginInputRef}
                                                type="text"
                                                name="login"
                                                value={login}
                                                onChange={(ev) => setLogin(ev.target.value)}
                                                className="border-start-0 py-2"
                                                placeholder="Introduce tu usuario"
                                                disabled={isLoading}
                                                autoComplete="username"
                                                style={{
                                                    borderRadius: '0 10px 10px 0',
                                                    fontSize: '1rem'
                                                }}
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="text-muted small fw-semibold text-uppercase">
                                            Contraseña
                                        </Form.Label>
                                        <div className="input-group">
                                            <span
                                                className="input-group-text bg-light border-end-0"
                                                style={{ borderRadius: '10px 0 0 10px' }}
                                            >
                                                <FiLock className="text-muted" />
                                            </span>
                                            <Form.Control
                                                type="password"
                                                name="password"
                                                value={password}
                                                onChange={(ev) => setPassword(ev.target.value)}
                                                className="border-start-0 py-2"
                                                placeholder="Introduce tu contraseña"
                                                disabled={isLoading}
                                                autoComplete="current-password"
                                                style={{
                                                    borderRadius: '0 10px 10px 0',
                                                    fontSize: '1rem'
                                                }}
                                            />
                                        </div>
                                    </Form.Group>

                                    <Button
                                        variant="primary"
                                        type="submit"
                                        className="w-100 py-3 mt-2 d-flex align-items-center justify-content-center"
                                        disabled={isLoading}
                                        onMouseEnter={() => setIsButtonHovered(true)}
                                        onMouseLeave={() => setIsButtonHovered(false)}
                                        style={{
                                            fontSize: '1.1rem',
                                            borderRadius: '10px',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            border: 'none',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px',
                                            opacity: isLoading ? 0.7 : 1,
                                            transition: 'all 0.2s ease',
                                            transform: isButtonHovered && !isLoading ? 'translateY(-2px)' : 'none',
                                            boxShadow: isButtonHovered && !isLoading
                                                ? '0 6px 20px rgba(102, 126, 234, 0.4)'
                                                : '0 2px 8px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                                Iniciando sesión...
                                            </>
                                        ) : (
                                            <>
                                                <FiLogIn className="me-2" />
                                                Iniciar Sesión
                                            </>
                                        )}
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>

                        {/* Version */}
                        <div className="text-center mt-4">
                            <small className="text-white-50">
                                {tre.version}
                            </small>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
}
